// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Import CSS files
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import './assets/css/global.css';
import './assets/css/index.css';

// Import Admin CSS
import './assets/css/admin/admin.css';
import './assets/css/admin/dashboard.css';
import './assets/css/admin/users.css';
import './assets/css/admin/events.css';
import './assets/css/admin/user-details.css';
import './assets/css/admin/event-details.css';
import './assets/css/admin/admin-dark-theme.css';
import './assets/css/dark-theme.css';
import './assets/css/admin/admin-override.css'; // Add this as the last import

// Pages and Components
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import EventPage from './pages/EventPage';
import CreateEventPage from './pages/CreateEventPage';
import ProfilePage from './pages/ProfilePage';
import MomentsPage from './pages/MomentsPage'; // User MomentsPage
import LoginForm from './components/forms/LoginForm';
import SignupForm from './components/forms/SignupForm';
import ForgotPasswordForm from './components/forms/ForgotPasswordForm';
import VerifyCodeForm from './components/forms/VerifyCodeForm';
import ResetPasswordForm from './components/forms/ResetPasswordForm';
import ContactPage from './pages/ContactPage';
import Header from './components/Header';
import LandingHeader from './components/LandingHeader';
import Footer from './components/Footer';
import MyEventsPage from './pages/MyEventsPage';
import SettingsPage from './pages/SettingsPage'; // Import the new SettingsPage component
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import UsersPage from './pages/admin/UsersPage';
import UserDetailsPage from './pages/admin/UserDetailsPage';
import EventsPage from './pages/admin/EventsPage';
import EventDetailsPage from './pages/admin/EventDetailsPage';
import CheckInsPage from './pages/admin/CheckInsPage';
import AdminMomentsPage from './pages/admin/MomentsPage'; // Rename to avoid conflict
import AdminProfilePage from './pages/admin/AdminProfilePage';
import MyProfilePage from './pages/admin/MyProfilePage'; // Rename the import to avoid conflict
import MomentDetailsPage from './pages/admin/MomentDetailsPage';
import AdminSettingsPage from './pages/admin/SettingsPage'; // Import the new AdminSettingsPage component

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { EventsProvider } from './context/EventsContext';
import { EventProvider } from './context/EventContext';
import { AdminProvider } from './context/AdminContext';

import PrivateRoute from './components/common/PrivateRoute';

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  const location = useLocation();
  if (!token) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  return children;
};

const App = () => {
  return (
    <AdminProvider>
      <AuthProvider>
        <EventsProvider>
          <Router>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route 
                path="/admin/dashboard" 
                element={
                  <AdminRoute>
                    <DashboardPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <AdminRoute>
                    <UsersPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/users/:userId" 
                element={
                  <AdminRoute>
                    <UserDetailsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/events" 
                element={
                  <AdminRoute>
                    <EventsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/events/:eventId" 
                element={
                  <AdminRoute>
                    <EventDetailsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/check-ins" 
                element={
                  <AdminRoute>
                    <CheckInsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/moments" 
                element={
                  <AdminRoute>
                    <AdminMomentsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/moments/:momentId" 
                element={
                  <AdminRoute>
                    <MomentDetailsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/profile" 
                element={<PrivateRoute component={MyProfilePage} />} 
              />
              <Route 
                path="/admin/settings" 
                element={
                  <AdminRoute>
                    <AdminSettingsPage />
                  </AdminRoute>
                } 
              />
              
              {/* Redirect /admin to /admin/dashboard */}
              <Route 
                path="/admin" 
                element={<Navigate to="/admin/dashboard" replace />} 
              />
              
              {/* Public Routes */}
              <Route path="/" element={
                <>
                  <LandingHeader />
                  <LandingPage />
                  <Footer />
                </>
              } />
              <Route path="/events" element={
                <>
                  <Header />
                  <HomePage />
                  <Footer />
                </>
              } />
              <Route path="/about" element={
                <>
                  <Header />
                  <AboutPage />
                  <Footer />
                </>
              } />
              <Route
                path="/event/:id"
                element={
                  <>
                    <Header />
                    <EventProvider>
                      <EventPage />
                    </EventProvider>
                    <Footer />
                  </>
                }
              />
              <Route path="/create-event" element={
                <>
                  <Header />
                  <CreateEventPage />
                  <Footer />
                </>
              } />
              <Route path="/my-events" element={
                <>
                  <Header />
                  <MyEventsPage />
                  <Footer />
                </>
              } />
              <Route path="/profile" element={
                <>
                  <Header />
                  <ProfilePage />
                  <Footer />
                </>
              } />
              <Route path="/moments" element={
                <>
                  <Header />
                  <MomentsPage />
                  <Footer />
                </>
              } />
              <Route path="/moments/:eventId" element={
                <>
                  <Header />
                  <MomentsPage />
                  <Footer />
                </>
              } />
              <Route path="/login" element={
                <>
                  <Header />
                  <LoginForm />
                  <Footer />
                </>
              } />
              <Route path="/signup" element={
                <>
                  <Header />
                  <SignupForm />
                  <Footer />
                </>
              } />
              <Route path="/forgot-password" element={
                <>
                  <Header />
                  <ForgotPasswordForm />
                  <Footer />
                </>
              } />
              <Route path="/verify-code" element={
                <>
                  <Header />
                  <VerifyCodeForm />
                  <Footer />
                </>
              } />
              <Route path="/reset-password" element={
                <>
                  <Header />
                  <ResetPasswordForm />
                  <Footer />
                </>
              } />
              <Route path="/contact" element={
                <>
                  <Header />
                  <ContactPage />
                  <Footer />
                </>
              } />
              <Route path="/settings" element={
                <>
                  <Header />
                  <SettingsPage />
                  <Footer />
                </>
              } />
              <Route path="/privacy-policy" element={
                <>
                  <Header />
                  <PrivacyPolicy />
                  <Footer />
                </>
              } />
              <Route path="/terms-of-service" element={
                <>
                  <Header />
                  <TermsOfService />
                  <Footer />
                </>
              } />
            </Routes>
          </Router>
        </EventsProvider>
      </AuthProvider>
    </AdminProvider>
  );
};

export default App;
